import { FC, PropsWithChildren } from "react";
import NavItem from "@/components/atoms/NavItem";
import styles from "./styles.module.css";
import { useGetFullContainers } from "@/services/LogisticsCoordinator";
import { useGetCompanyById } from "@/services/Company";
import { useAuth } from "@/contexts/AuthContext";
import { getRuntimeConfig } from "@/utils/runtimeConfig";

export interface NavigationItem {
  id: string;
  url: string;
  translationsKey: string;
  hidden?: boolean;
  children?: NavigationItem[];
  superscript?: string;
  /** Per-child count badge shown in the rich dropdown. */
  count?: number;
  /** Per-child path sub-label shown in the rich dropdown. */
  subLabel?: string;
  /** Render children as the rich dropdown (counts + sub-labels + aqua active accent). */
  richDropdown?: boolean;
}

interface NavigationProps {
  items: NavigationItem[];
  variant?: "primary" | "secondary";
  taskCount?: number;
}

interface Props {
  taskCount?: number;
}

const adminNavItems: NavigationItem[] = [
  {
    id: "adminCompanies",
    url: "/adminCompanies",
    translationsKey: "navigation:companies",
  },
  {
    id: "statistics",
    url: "/statistics",
    translationsKey: "navigation:statistics",
  },
  {
    id: "staleTransactions",
    url: "/staleTransactions",
    translationsKey: "navigation:staleTransactions",
  },
  {
    id: "adminFeedback",
    url: "/adminFeedback",
    translationsKey: "navigation:adminFeedback",
  },
  {
    id: "adminClaims",
    url: "/adminClaims",
    translationsKey: "navigation:adminClaims",
  },
  {
    id: "adminReports",
    url: "/adminReports",
    translationsKey: "navigation:adminReports",
  },
];

const mainNavItems: NavigationItem[] = [
  // {
  //   id: "dashboard",
  //   url: "/dashboard",
  //   translationsKey: "navigation:home",
  // },
  {
    id: "market",
    url: "/market",
    translationsKey: "common:market",
    hidden: getRuntimeConfig("ENABLED_MARKET") !== "true",
  },
  {
    id: "contracts",
    url: "/contracts",
    translationsKey: "navigation:contracts",
  },
  {
    id: "task",
    url: "/task",
    translationsKey: "navigation:tasks",
  },
  {
    id: "orders",
    url: "/myOrders",
    translationsKey: "navigation:orders",
  },
  {
    // Only shown when the company has receiving inspection on; filtered in BaseNavigation.
    id: "inspections",
    url: "/inspections",
    translationsKey: "navigation:inspections",
  },
  {
    id: "containers",
    url: "/containers",
    translationsKey: "navigation:containers",
    // superscript + Collections count are filled with live data in BaseNavigation.
    richDropdown: true,
    children: [
      {
        id: "containersCollections",
        url: "/collections",
        translationsKey: "navigation:containersCollections",
        subLabel: "/containers/collections",
      },
      {
        id: "containersScheduled",
        url: "/scheduled",
        translationsKey: "navigation:containersScheduled",
        subLabel: "/containers/scheduled",
      },
    ],
  },
  {
    id: "more",
    // No path of its own: children carry their full URLs.
    url: "",
    translationsKey: "navigation:more",
    richDropdown: true,
    children: [
      {
        id: "moreArchive",
        url: "/invoiceArchive",
        translationsKey: "navigation:archive",
      },
      {
        id: "moreWeightReports",
        url: "/matchWeightReports",
        translationsKey: "weightReports:weightReports",
      },
      {
        id: "moreReports",
        url: "/reports",
        translationsKey: "navigation:reports",
        hidden: getRuntimeConfig("APACHE_VISIBILITY") !== "true",
      },
    ],
  },
];

const mainNavItemsLimited: NavigationItem[] = [
  {
    id: "dashboard",
    url: "/dashboard",
    translationsKey: "navigation:home",
  },
  {
    id: "contracts",
    url: "/contracts",
    translationsKey: "navigation:contracts",
  },
];

export const BaseNavigation: FC<PropsWithChildren<NavigationProps>> = ({ items, variant, taskCount }) => {
  // Only fetch the Containers queue when this nav actually renders the containers item.
  const hasContainers = items.some((item) => item.id === "containers");
  const { data: fullContainers } = useGetFullContainers({ enabled: hasContainers });
  const collectionsCount = fullContainers?.containers.length;

  // Inspections only shows for companies with receiving inspection turned on (Settings → Receiving inspection).
  const { currentCompany } = useAuth();
  const hasInspections = items.some((item) => item.id === "inspections");
  const { data: company } = useGetCompanyById(currentCompany?.companyId as number, {
    enabled: hasInspections && !!currentCompany?.companyId,
  });
  const visibleItems = company?.isReceivingInspectionEnabled
    ? items
    : items.filter((item) => item.id !== "inspections");

  return (
    <nav className={`${styles.navRoot} ${variant !== "primary" && styles[variant as keyof typeof styles]}`}>
      {visibleItems.map((item, i) => {
        let navItem = item;
        // Counts show once loaded, including 0, so an empty queue reads as empty rather than unknown.
        // Surface the live open-tasks count as the Tasks superscript.
        if (item.id === "task" && taskCount !== undefined) {
          navItem = { ...item, superscript: String(taskCount) };
        }
        // Surface the live Collections list length on the Containers item + its Collections child.
        else if (item.id === "containers" && collectionsCount !== undefined) {
          navItem = {
            ...item,
            superscript: String(collectionsCount),
            children: item.children?.map((child) =>
              child.id === "containersCollections" ? { ...child, count: collectionsCount } : child
            ),
          };
        }
        return <NavItem navItem={navItem} key={i} />;
      })}
    </nav>
  );
};

export const Navigation: FC<Props> = (props) => <BaseNavigation items={mainNavItems} variant="primary" {...props} />;
export const NavigationLimited: FC<Props> = (props) => (
  <BaseNavigation items={mainNavItemsLimited} variant="secondary" {...props} />
);
export const AdminNavigation: FC<Props> = () => <BaseNavigation items={adminNavItems} variant="secondary" />;

// export default Navigation;
